import { useState, useCallback, useRef } from "react";
import axios from "redaxios";
import { apiConfig } from "../lib/apiConfig";
import { trackEvent } from "../lib/analytics";

function useFileUploader(sessionToken) {
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ speed: 0, eta: 0 });

  const uploadIdRef = useRef(0);
  const abortControllerRef = useRef(new AbortController());
  const xhrRef = useRef(null);

  /**
   * Cancel the ongoing upload
   */
  const abortUpload = useCallback(() => {
    // If using fetch with AbortController:
    abortControllerRef.current.abort();
    // If using XHR, call xhrRef.current.abort():
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
    abortControllerRef.current = new AbortController(); // new controller for future requests
    setUploadProgress(0);
    setIsUploading(false);
    setUploadError("Upload cancelled");
    setUploadStats({ speed: 0, eta: 0 });
    uploadIdRef.current = 0;
    trackEvent("file_upload_cancelled");
  }, []);

  /**
   * Helper: format bytes to a readable string
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  /**
   * Helper: format duration in seconds to mm:ss
   */
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  /**
   * uploadToS3
   * - This calls our "signedUrl" lambda to get a presigned S3 PUT URL
   * - Then uploads the file via XHR so we can monitor progress & cancel
   */
  const uploadToS3 = useCallback(async (file, userInfo) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    setUploadStats({ speed: 0, eta: 0 });
    const newUploadId = Date.now();
    uploadIdRef.current = newUploadId;
    const startTime = Date.now();

    try {
      // Check if we're in development mode
      const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const enableRealCheckout = import.meta.env.VITE_ENABLE_REAL_CHECKOUT === 'true';
      
      let uploadURL, userId;
      
      if (isDev && !enableRealCheckout && !window.location.href.includes('vercel.app')) {
        // Development mode - simulate upload
        console.log('[DEV] Simulating file upload for local development');
        userId = `dev-user-${Date.now()}`;
        
        // Simulate upload progress
        const simulateProgress = () => {
          return new Promise((resolve) => {
            let progress = 0;
            const interval = setInterval(() => {
              progress += 10;
              if (uploadIdRef.current !== newUploadId) {
                clearInterval(interval);
                return;
              }
              setUploadProgress(progress);
              
              // Simulate speed stats
              const elapsed = Date.now() - startTime;
              const speed = (file.size * (progress / 100)) / (elapsed / 1000);
              const remaining = ((100 - progress) / 10) * 0.1; // seconds
              setUploadStats({
                speed: `${formatBytes(speed)}/s`,
                eta: formatDuration(remaining),
              });
              
              if (progress >= 100) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        };
        
        await simulateProgress();
        
        return {
          statusCode: 200,
          userId,
          filePattern: `dev-${file.name}`,
        };
      } else {
        // Production mode - use real API
        const response = await axios.post('/api/get-upload-url', {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
        }, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          },
          signal: abortControllerRef.current.signal,
        });

        if (!response.data?.uploadURL) {
          throw new Error("Server response missing S3 upload URL");
        }

        uploadURL = response.data.uploadURL;
        userId = response.data.userId;
      }

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener("progress", (event) => {
          if (!event.lengthComputable) return;
          if (uploadIdRef.current !== newUploadId) return;

          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);

          // Speed stats
          const elapsed = Date.now() - startTime;
          const speed = event.loaded / (elapsed / 1000);
          const remaining = (event.total - event.loaded) / speed;
          setUploadStats({
            speed: `${formatBytes(speed)}/s`,
            eta: formatDuration(remaining),
          });
        });

        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 0) {
              // Usually means the user aborted
              reject(new Error("Upload cancelled"));
            } else if (xhr.status >= 200 && xhr.status < 300) {
              // Attempt to parse the final file key or pattern
              const responseURL = xhr.responseURL;
              // For example: 2023-08-01_103000_name_request_1.csv
              // Or we can rely on the server giving us a known pattern
              let filePattern = "";
              try {
                const urlSegments = responseURL.split("?");
                filePattern = urlSegments[0].split("/").pop() || "";
              } catch (e) {
                filePattern = "unknown_pattern.csv";
              }
              resolve({
                statusCode: xhr.status,
                userId,
                filePattern,
              });
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
            setIsUploading(false);
          }
        };

        xhr.open("PUT", uploadURL);
        xhr.setRequestHeader("Content-Type", "text/csv");
        xhr.send(file);
      });
    } catch (error) {
      if (error.name === "AbortError") {
        // user triggered an abort
        setUploadError("Upload cancelled by user");
        setUploadProgress(0);
        setIsUploading(false);
        trackEvent("file_upload_cancelled");
      } else {
        setUploadError(error.message);
        setIsUploading(false);
        trackEvent("file_upload_error", { error_message: error.message });
        throw error;
      }
      return { statusCode: 0, userId: null, filePattern: "" };
    }
  }, [sessionToken]);

  return {
    uploadToS3,
    abortUpload,
    uploadError,
    uploadProgress,
    isUploading,
    uploadStats,
  };
}

export default useFileUploader;
